import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProject = {
  title_de: '', title_en: '', title_es: '',
  description_de: '', description_en: '', description_es: '',
  category: 'hallenbau', images: [], location: '', year: '', area: '', client_name: '', featured: false,
};

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyProject });
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/projects`, { headers }).then(r => setProjects(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyProject }); setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setForm({ ...p, images: p.images || [] }); setEditing(p.id); setDialogOpen(true); };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/projects/${editing}`, form, { headers });
        toast.success('Projekt aktualisiert');
      } else {
        await axios.post(`${API}/admin/projects`, form, { headers });
        toast.success('Projekt erstellt');
      }
      setDialogOpen(false);
      load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Projekt wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, { headers });
      toast.success('Projekt gelöscht');
      load();
    } catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A]">Projekte verwalten</h1>
        <Button onClick={openNew} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" /> Neues Projekt
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titel (DE)</TableHead>
              <TableHead>Kategorie</TableHead>
              <TableHead>Standort</TableHead>
              <TableHead>Jahr</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map(p => (
              <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                <TableCell className="font-medium">{p.title_de}</TableCell>
                <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{p.location}</TableCell>
                <TableCell className="text-sm text-slate-500">{p.year}</TableCell>
                <TableCell>{p.featured ? <Badge className="bg-[#066ce7] text-white">Ja</Badge> : '-'}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-500" data-testid={`delete-project-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Projekt bearbeiten' : 'Neues Projekt'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Titel DE</Label><Input value={form.title_de} onChange={e => updateForm('title_de', e.target.value)} data-testid="project-title-de" /></div>
              <div><Label className="text-xs">Titel EN</Label><Input value={form.title_en} onChange={e => updateForm('title_en', e.target.value)} data-testid="project-title-en" /></div>
              <div><Label className="text-xs">Titel ES</Label><Input value={form.title_es} onChange={e => updateForm('title_es', e.target.value)} data-testid="project-title-es" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Beschreibung DE</Label><Textarea value={form.description_de} onChange={e => updateForm('description_de', e.target.value)} rows={2} /></div>
              <div><Label className="text-xs">Beschreibung EN</Label><Textarea value={form.description_en} onChange={e => updateForm('description_en', e.target.value)} rows={2} /></div>
              <div><Label className="text-xs">Beschreibung ES</Label><Textarea value={form.description_es} onChange={e => updateForm('description_es', e.target.value)} rows={2} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kategorie</Label>
                <Select value={form.category} onValueChange={v => updateForm('category', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hallenbau">Hallenbau</SelectItem>
                    <SelectItem value="fenster">Fenster</SelectItem>
                    <SelectItem value="tueren">Türen</SelectItem>
                    <SelectItem value="fallschutz">Fallschutz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Standort</Label><Input value={form.location} onChange={e => updateForm('location', e.target.value)} /></div>
              <div><Label className="text-xs">Jahr</Label><Input value={form.year} onChange={e => updateForm('year', e.target.value)} /></div>
              <div><Label className="text-xs">Flache</Label><Input value={form.area} onChange={e => updateForm('area', e.target.value)} /></div>
              <div><Label className="text-xs">Kunde</Label><Input value={form.client_name} onChange={e => updateForm('client_name', e.target.value)} /></div>
              <div className="flex items-center gap-3 pt-5">
                <Switch checked={form.featured} onCheckedChange={v => updateForm('featured', v)} data-testid="project-featured-switch" />
                <Label className="text-xs">Featured</Label>
              </div>
            </div>
            <div>
              <ImageUpload value={form.images} onChange={v => updateForm('images', v)} multiple={true} label="Projektbilder" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#066ce7] text-white" data-testid="save-project-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
